import React, { useEffect, useState, useRef } from 'react';
import { db } from '../db';
import { processSyncQueue } from '../sync/syncEngine';
import './UpdateNotification.css';

interface UpdateNotificationProps {
  registration: ServiceWorkerRegistration | null;
}

const COUNTDOWN_SECONDS = 10;

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ registration }) => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasTriggeredRef = useRef(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Modifiche locali ancora da caricare sul server. Se il DB non è leggibile
  // restituiamo 1 (prudenza): meglio non cancellare che cancellare alla cieca.
  const countPendingChanges = async (): Promise<number> => {
    try {
      return await db.syncQueue.where('synced').equals(0).count();
    } catch {
      return 1;
    }
  };

  const clearIndexedDB = async (): Promise<void> => {
    try {
      if ('databases' in indexedDB) {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name) {
            await new Promise<void>((resolve) => {
              const request = indexedDB.deleteDatabase(db.name!);
              request.onsuccess = () => resolve();
              request.onerror = () => resolve();
              request.onblocked = () => resolve();
            });
          }
        }
      } else {
        await new Promise<void>((resolve) => {
          const request = indexedDB.deleteDatabase('MappingDatabase');
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
          request.onblocked = () => resolve();
        });
      }
    } catch {
      // continue with update even if this fails
    }
  };

  const clearCaches = async (): Promise<void> => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch {
      // continue with update even if this fails
    }
  };

  const clearLocalStorage = (): void => {
    try {
      localStorage.clear();
    } catch {
      // continue with update even if this fails
    }
  };

  const handleUpdate = async () => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    setIsUpdating(true);

    try {
      // ⚠️ SALVAGUARDIA DATI — non rimuovere.
      // clearIndexedDB() cancella TUTTO il database locale: le voci e le foto
      // raccolte in cantiere e non ancora caricate sul server sparirebbero per
      // sempre, senza alcun avviso e con l'aggiornamento che parte da solo dopo
      // il countdown. Succede davvero: dati di una giornata di cantiere persi.
      // Se restano modifiche in sospeso l'app si aggiorna comunque, ma i dati
      // locali NON vengono toccati.
      const pending = await countPendingChanges();
      let stillPending = pending;

      if (pending > 0) {
        setPendingCount(pending);
        if (navigator.onLine) {
          try {
            await processSyncQueue();
          } catch {
            // upload fallito: teniamo comunque i dati locali
          }
          stillPending = await countPendingChanges();
        }
      }

      if (stillPending > 0) {
        console.warn(
          `[update] ${stillPending} modifiche non sincronizzate: database locale preservato, nessuna cancellazione`
        );
        setPendingCount(stillPending);
      } else {
        await clearIndexedDB();
      }

      await clearCaches();
      clearLocalStorage();

      const target = registration?.waiting ?? (await navigator.serviceWorker.ready).waiting;
      if (target) {
        target.postMessage({ type: 'SKIP_WAITING' });
      }

      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });

      setTimeout(() => {
        if (!refreshing) window.location.reload();
      }, 2000);
    } catch {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (!registration) return;

    setShowUpdate(true);
    setCountdown(COUNTDOWN_SECONDS);
    hasTriggeredRef.current = false;

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          handleUpdate();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration]);

  if (!showUpdate) return null;

  return (
    <div className="update-notification">
      <div className="update-content">
        <div className="update-icon">{isUpdating ? '⏳' : '🔄'}</div>
        <div className="update-text">
          <strong>{isUpdating ? 'Aggiornamento in corso...' : 'Nuova versione disponibile!'}</strong>
          <p>
            {isUpdating
              ? (pendingCount > 0
                  ? `Aggiornamento in corso. ${pendingCount} modifiche non ancora sincronizzate: i dati sul dispositivo vengono conservati.`
                  : 'Pulizia dati e ricaricamento app...')
              : `Aggiornamento automatico tra ${countdown}s. I dati locali verranno riscaricati dal server.`}
          </p>
        </div>
        {!isUpdating && (
          <div className="update-actions">
            <button
              className="update-btn update-btn-primary"
              onClick={handleUpdate}
            >
              Aggiorna Ora
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateNotification;
